<template>
  <div class="text-start">
    <div class="btn-group btn-group-sm mb-2" role="group">
      <button
        type="button"
        class="btn"
        :class="mode === 'url' ? 'btn-primary' : 'btn-outline-secondary'"
        @click="mode = 'url'"
      >
        Paste URL
      </button>
      <button
        type="button"
        class="btn"
        :class="mode === 'upload' ? 'btn-primary' : 'btn-outline-secondary'"
        @click="mode = 'upload'"
      >
        Upload
      </button>
    </div>

    <template v-if="mode === 'url'">
      <label class="form-label small text-muted d-none">Image URL</label>
      <input
        type="url"
        class="form-control form-control-sm mb-2"
        v-model="urlValue"
        placeholder="https://..."
        @input="urlPreviewOk = null"
      />
      <div v-if="urlValue" class="mb-2 text-center">
        <img
          :src="urlValue"
          class="img-thumbnail"
          style="max-height: 150px"
          @load="urlPreviewOk = true"
          @error="urlPreviewOk = false"
        />
        <div v-if="urlPreviewOk === false" class="text-danger small mt-1">
          Couldn't load that image. The host may block hotlinking.
        </div>
      </div>
    </template>

    <template v-else>
      <label class="form-label small text-muted d-none">Choose a file</label>
      <input
        type="file"
        class="form-control form-control-sm mb-2"
        :accept="accept"
        @change="onFileSelected"
      />
      <div v-if="filePreviewUrl" class="mb-2 text-center">
        <img :src="filePreviewUrl" class="img-thumbnail" style="max-height: 150px" />
      </div>
      <div v-if="fileError" class="text-danger small mb-2">{{ fileError }}</div>
    </template>

    <div v-if="error" class="text-danger small mb-2">{{ error }}</div>

    <div class="d-flex gap-2 justify-content-center">
      <button
        class="btn btn-sm btn-primary"
        :disabled="!canSave || saving"
        @click="save"
      >
        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
        Save
      </button>
      <button class="btn btn-sm btn-outline-secondary" @click="$emit('cancel')">Cancel</button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue'

const props = defineProps({
  // async (file) => { uploadUrl, publicUrl, contentType }
  requestUploadUrl: {
    type: Function,
    required: true
  },
  // async (url) => void — persists the resolved image URL (e.g. to inventory/eshop/etc)
  onResolved: {
    type: Function,
    required: true
  },
  accept: {
    type: String,
    default: 'image/jpeg,image/png,image/webp,image/gif'
  },
  maxSizeMb: {
    type: Number,
    default: 15
  }
})

const emit = defineEmits(['saved', 'cancel'])

const mode = ref('url')

const urlValue = ref('')
const urlPreviewOk = ref(null)

const selectedFile = ref(null)
const filePreviewUrl = ref(null)
const fileError = ref(null)

const saving = ref(false)
const error = ref(null)

const canSave = computed(() => {
  if (mode.value === 'url') return !!urlValue.value && urlPreviewOk.value === true
  return !!selectedFile.value && !fileError.value
})

function onFileSelected(event) {
  fileError.value = null
  filePreviewUrl.value = null
  selectedFile.value = null

  const file = event.target.files?.[0]
  if (!file) return

  if (!props.accept.split(',').includes(file.type)) {
    fileError.value = 'Unsupported file type.'
    return
  }
  if (file.size > props.maxSizeMb * 1024 * 1024) {
    fileError.value = `File is too large (max ${props.maxSizeMb}MB).`
    return
  }

  selectedFile.value = file
  filePreviewUrl.value = URL.createObjectURL(file)
}

async function save() {
  saving.value = true
  error.value = null
  try {
    let resolvedUrl = urlValue.value

    if (mode.value === 'upload') {
      const { uploadUrl, publicUrl, contentType } = await props.requestUploadUrl(selectedFile.value)
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: selectedFile.value
      })
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`)
      }
      resolvedUrl = publicUrl
    }

    await props.onResolved(resolvedUrl)
    emit('saved')
  } catch (err) {
    error.value = err.response?.data?.error || err.message || 'Failed to save image'
  } finally {
    saving.value = false
  }
}
</script>
